/* istanbul ignore file */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Tabs } from '@interstate/components/Tabs';
import { Box } from '@interstate/components/Box';
import { TextInput } from '@interstate/components/TextInput';

import { FourSquareGeneralTab } from './FourSquareGeneralTab';
import { FourSquareDisplayTab } from './FourSquareDisplayTab';
import { TEXT_INPUT_MAX_LENGTH } from './constants';
import { useProposalForm } from './useProposalForm';
import type { ProposalFormPayload } from './useProposalForm';
import { hasFourSquareFieldLabel, hasFourSquareDisplayLabel } from './utils';
import { useDataLoadingState } from '../../utils/useDataLoadingState';
import { LoadingSection } from '@makemydeal/dr-shared-components';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { Toast } from '../../common/DataLoadingToast';
import type { ToastType } from '../../common/DataLoadingToast/Toast';

export interface CreateProposalHandle {
    getFormData: () => ProposalFormPayload;
}

const noop = () => {};

type CreateFourSquareProposalProps = {
    shouldDisableSubmit?: (hasError: boolean) => void;
    setView: (view: 'list' | 'create' | 'edit' | 'preview' | 'none') => void;
    setEditMode?: (isEditMode: boolean) => void;
    isEditMode?: boolean;
    isViewMode?: boolean;
};

export const CreateFourSquareProposal = forwardRef<CreateProposalHandle, CreateFourSquareProposalProps>(
    ({ shouldDisableSubmit: setHasValidationError = noop, setView, isEditMode, setEditMode = noop, isViewMode }, ref) => {
        const isCarsActDisclosuresApplicable = useSelector(dealerSelectors.isCarsActDisclosuresApplicable);

        const displayKeyFilter = useCallback(
            (key: string) => {
                if (key === 'carsActSpanishDisclosure' && (!isCarsActDisclosuresApplicable || isViewMode)) {
                    return false;
                }
                return hasFourSquareDisplayLabel(key);
            },
            [isCarsActDisclosuresApplicable, isViewMode]
        );

        const form = useProposalForm({
            isEditMode,
            fieldKeyFilter: hasFourSquareFieldLabel,
            displayKeyFilter
        });

        useDataLoadingState({
            isLoading: form.isCreatingProposal,
            isError: form.hasCreationError,
            isLoaded: form.isCreationSuccess,
            onLoaded: () => {
                setEditMode(!!isEditMode);
                setView('preview');
            }
        });

        useDataLoadingState({
            isLoading: form.isUpdatingProposal,
            isError: form.hasUpdateError,
            isLoaded: form.isUpdateSuccess,
            onLoaded: () => {
                setEditMode(!!isEditMode);
                setView('preview');
            }
        });

        useEffect(() => {
            setHasValidationError(form.hasError);
        }, [form.hasError]);

        useImperativeHandle(
            ref,
            () => ({
                getFormData: form.getFormData
            }),
            [form.getFormData]
        );

        const tabs = useMemo(
            () => [
                {
                    label: 'General',
                    active: form.activeTab === 'general',
                    value: 'general',
                    component: (
                        <FourSquareGeneralTab
                            fieldConfiguration={form.fieldConfiguration as any}
                            onActiveChange={form.handleActiveChangeForFieldConfigItem as any}
                            disabled={isViewMode}
                        />
                    )
                },
                {
                    label: 'Display',
                    active: form.activeTab === 'display',
                    value: 'display',
                    component: (
                        <FourSquareDisplayTab
                            displayConfiguration={form.displayConfiguration as any}
                            onDisplayActiveChange={form.handleActiveChangeForDisplayConfigItem as any}
                            onDisplayValueChange={form.handleValueChangeForDisplayConfigItem as any}
                            onDisplayNameChange={form.handleDisplayNameChangeForDisplayConfigItem as any}
                            onDollarValueRoundingChange={form.handleDollarValueRoundingChange}
                            disabled={isViewMode}
                        />
                    )
                }
            ],
            [
                form.activeTab,
                form.fieldConfiguration,
                form.displayConfiguration,
                form.handleActiveChangeForFieldConfigItem,
                form.handleActiveChangeForDisplayConfigItem,
                form.handleValueChangeForDisplayConfigItem,
                form.handleDisplayNameChangeForDisplayConfigItem,
                form.handleDollarValueRoundingChange,
                isViewMode
            ]
        );

        const getErrorMessage = (type: ToastType) => {
            if (isEditMode && type === 'error' && form.hasUpdateError) {
                return form.updateErrorMessage || 'Error saving printing proposal.';
            }
            if (!isEditMode && type === 'error' && form.hasCreationError) {
                return form.creationErrorMessage || 'Error creating printing proposal.';
            }
            return undefined;
        };

        return (
            <Box data-testid="create-proposal-component" sx={{ display: 'flex', flexDirection: 'column' }}>
                <LoadingSection
                    loading={
                        form.isLoadingProposal ||
                        form.isCreatingProposal ||
                        form.isUpdatingProposal ||
                        (!isEditMode && !form.proposalToEdit && !form.isProposalError)
                    }
                />
                <Box mb={2}>
                    <Toast
                        isLoading={form.isCreatingProposal || form.isUpdatingProposal}
                        isLoaded={form.isCreationSuccess || form.isUpdateSuccess}
                        isError={form.hasCreationError || form.hasUpdateError}
                        getMessage={getErrorMessage}
                    />
                </Box>
                <Box sx={{ mb: 2 }}>
                    <TextInput
                        label="Proposal Name"
                        name="proposal-name"
                        required
                        maxLength={TEXT_INPUT_MAX_LENGTH}
                        customErrorMessages={{
                            exceedsMaxLength: `Value exceeds ${TEXT_INPUT_MAX_LENGTH} characters.`
                        }}
                        value={form.proposalName}
                        onChange={(e) => form.setProposalName(e.target.value)}
                        size="small"
                        data-testid="proposal-name-input"
                        disabled={isViewMode}
                    />
                </Box>
                <Tabs
                    id="create-proposal-tabs"
                    data-testid="create-proposal-tabs"
                    onActivate={(tab) => {
                        form.setActiveTab(tab);
                    }}
                    tabs={tabs}
                />
            </Box>
        );
    }
);

CreateFourSquareProposal.displayName = 'CreateFourSquareProposal';
